/**
 * Completion for the DSL editor — what can follow what you have typed.
 *
 * Pure: given text, a caret and the vocabulary, it returns the word being typed
 * and the candidates for it. The component owns the popup and the keys; this
 * owns the decisions, which is what makes them testable.
 *
 * Deliberately prefix-only, not fuzzy. A screen carries ~120 columns with names
 * like `sma17Support` and `kahunaUp`; fuzzy matching over that set surfaces
 * plausible-but-wrong entries at the top, and picking the wrong column is a
 * silent error — the expression compiles and the number is meaningless. Prefix
 * matching is predictable, and the reference panel is there for search by
 * meaning.
 */

import { callInsertionFor, type DslCatalog, type DslFunction } from "./dsl-catalog.js";

export interface Completion {
  /** What gets inserted. */
  value: string;
  /** `function`, `column`, or the alias's target for a shorthand. */
  detail: string;
  kind: "function" | "column" | "alias";
  /**
   * The catalog entry this came from, when it is a function.
   *
   * Carried so {@link applyCompletion} can insert a *call* where there is no
   * receiver — it needs the arity and the window default, and re-looking it up
   * would mean passing the catalog to a function whose whole job is string
   * surgery. Absent for columns and aliases, and absent when a caller builds a
   * `Completion` by hand.
   */
  fn?: DslFunction;
}

export interface CompletionQuery {
  /** The partial word under the caret. Empty means no completion is offered. */
  prefix: string;
  /** Replace this range with the chosen value. */
  from: number;
  to: number;
  items: Completion[];
}

/** Characters that can appear inside an identifier, including a leading digit. */
const WORD = /[A-Za-z0-9_]/;

/**
 * The identifier the caret sits in or at the end of.
 *
 * A word starting immediately after `.` is a *postfix* position — the frequency
 * or method after a receiver — which is why the dot is not part of the word:
 * completing `c.sm` should offer `sma`, not `c.sma`.
 */
export function wordAt(text: string, caret: number): { word: string; from: number } {
  let from = caret;
  while (from > 0 && WORD.test(text[from - 1]!)) from -= 1;
  return { word: text.slice(from, caret), from };
}

export function completionsFor(
  text: string,
  caret: number,
  catalog: DslCatalog,
  fields: readonly string[] = [],
  limit = 12,
): CompletionQuery {
  const { word, from } = wordAt(text, caret);
  const empty: CompletionQuery = { prefix: word, from, to: caret, items: [] };
  // Nothing typed yet: offering the whole vocabulary on every keystroke is a
  // popup that is always open and never useful.
  if (word.length === 0) return empty;

  const needle = word.toLowerCase();
  const items: Completion[] = [];

  for (const fn of catalog.functions) {
    // Case-insensitive, because function names are no longer all lowercase:
    // `macdSignal` and `bbandsUpper` name one output of a multi-output
    // indicator, and typing "macds" has to reach them. Columns already matched
    // this way (`sma17Support`); functions now need to as well.
    if (fn.name.toLowerCase().startsWith(needle)) {
      items.push({
        value: fn.name,
        kind: "function",
        detail: fn.series ? "series · server" : "scalar",
        fn,
      });
    }
  }
  for (const [alias, target] of Object.entries(catalog.fieldAliases)) {
    if (alias.startsWith(needle)) items.push({ value: alias, kind: "alias", detail: target });
  }
  for (const field of fields) {
    if (field.toLowerCase().startsWith(needle)) {
      items.push({ value: field, kind: "column", detail: "column" });
    }
  }

  // An exact match first, then shortest — `sma` before `sma17Support`, because
  // the shorter name is the one someone typing three letters usually meant.
  items.sort((a, b) => {
    const exact = Number(b.value.toLowerCase() === needle) - Number(a.value.toLowerCase() === needle);
    return exact || a.value.length - b.value.length || a.value.localeCompare(b.value);
  });

  // One row per name.
  //
  // The three loops above draw from three vocabularies that are allowed to
  // overlap: a screen can carry a column named exactly like a function, and then
  // `sma` was offered twice — identically, so there was no way to tell which row
  // did what, and React warned about the duplicate key besides.
  //
  // The **function wins**, because that is what the evaluator does with a bare
  // name: a call is resolved before a column of the same name, so offering the
  // column would complete to something that does not mean what it says. Kept in
  // push order (function, alias, column) rather than re-ranked, since the sort
  // above has already put the best match first within each.
  const seen = new Set<string>();
  const unique = items.filter((item) => {
    const key = item.value.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return { ...empty, items: unique.slice(0, limit) };
}

/** Apply a completion, returning the new text and where the caret lands. */
export function applyCompletion(
  text: string,
  query: CompletionQuery,
  choice: Completion,
): { text: string; caret: number } {
  // After a `.` the receiver is already there, so the bare name is the whole
  // completion — `c.` + `paSignal` is a call, and appending arguments to it
  // would produce `c.paSignal(c)`. A series function is completed to its bare
  // name rather than `sma20` here for the original reason too: the typist is
  // mid-word and about to type the window, and inserting one would put digits
  // after their caret that they then have to delete.
  const postfix = query.from > 0 && text[query.from - 1] === ".";

  // With no receiver, a bare name is a *column*, not a call — so a function
  // picked from the list has to arrive with its parentheses or it silently
  // becomes a lookup of a column nobody has. See `callInsertionFor`.
  if (!postfix && choice.kind === "function" && choice.fn) {
    const insertion = callInsertionFor(choice.fn);
    return {
      text: text.slice(0, query.from) + insertion.text + text.slice(query.to),
      caret: query.from + insertion.caret,
    };
  }

  const next = text.slice(0, query.from) + choice.value + text.slice(query.to);
  return { text: next, caret: query.from + choice.value.length };
}
